import {useState, useEffect, useReducer} from 'react';
import {useInputMenuContext} from 'context/InputMenu';
import {SWRKeyEnum, inputMenuActionType} from 'interfaces';

import addEditHarvestBackendHandler, {initialState, handleCommunicationSuccess} from 'helpers/addEdit/harvest';

import useParseURL from 'hooks/useParseURL';
import {useTranslation} from 'react-i18next';
import {useGetForecastState, useGetHarvest, useGetHarvestPlantAvailable, useGetPlan} from 'hooks/swr';
import {dateReader} from 'helpers/UnitsFormatHandler';
import useGetLongTermRange from 'hooks/useGetLongTermRange';
import {mutateCage, mutateSite} from 'helpers/refreshHandler';
import {add} from 'date-fns';
import {actionTypeEnum, reducer} from '../../components/InputMenu/Harvest/reducer';
import {mutateAll, type SWRresourceId} from '../swr/swrHookFactory';

function useAddCageHarvest(isEmergencyHarvest = false) {
  const {planId} = useParseURL();

  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const method = inputMenuState?.method;
  const inputMenuDispatch = inputMenuContext?.dispatch;

  const formData = inputMenuState?.formData;
  const _initialState = initialState(formData);
  _initialState.isEmergency = isEmergencyHarvest;

  const [state, dispatch] = useReducer(reducer, {
    ..._initialState,
  });
  const forecastResourceId: SWRresourceId | undefined =
    state.date && state.cageId ? [state.date, state.cageId] : undefined;
  const {data: forecast} = useGetForecastState({resourceId: forecastResourceId});
  useEffect(() => {
    forecast && dispatch({type: actionTypeEnum.setForecast, forecast});
  }, [forecast]);

  const {t} = useTranslation('common');

  const {data: harvestToEdit} = useGetHarvest({resourceId: state.id});

  const [createUpdateCageHarvest, setCreateUpdateCageHarvest] = useState<boolean>(false);
  const {startDate, endDate} = useGetLongTermRange();
  const {data: plan} = useGetPlan({resourceId: planId});
  const {data: harvestPlants} = useGetHarvestPlantAvailable({resourceId: state.siteId});
  const availableHarvestPlants = harvestPlants?.available ?? [];

  const planStartDate = plan && dateReader(plan?.startDate);
  const contextChartEndDate = startDate && add(startDate, {months: 6});

  useEffect(() => {
    if (harvestToEdit) {
      dispatch({type: actionTypeEnum.setHarvest, harvestToEdit});
    }
  }, [harvestToEdit]);

  useEffect(() => {
    method &&
      createUpdateCageHarvest &&
      startDate &&
      endDate &&
      addEditHarvestBackendHandler({
        state,
        method,
        createUpdateCageHarvest,
        setCreateUpdateCageHarvest,
        communicationSuccess: (newHarvest) => {
          handleCommunicationSuccess({
            setCreateUpdateCageHarvest,
            dispatch,
            cageId: state.cageId ?? null,
            date: state.date,
          });
          inputMenuState?.onClose && inputMenuState.onClose(newHarvest);
          inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});

          mutateAll(SWRKeyEnum.GET_PLAN_HARVESTS);
          mutateAll(SWRKeyEnum.GET_PLAN_ACTIONS);
          mutateAll(SWRKeyEnum.GET_PLAN_HARVESTS_WITH_DISTRIBUTIONS);

          if (planStartDate && contextChartEndDate && state.siteId) {
            state?.cageId && mutateCage(state.cageId);
            mutateSite(state.siteId);
          }
        },
        t,
      });
  }, [createUpdateCageHarvest]);

  useEffect(() => {
    if (!state.id && harvestPlants && harvestPlants?.preferred) {
      dispatch({type: actionTypeEnum.setProperties, harvestPlantId: harvestPlants?.preferred.id});
    }
  }, [state.id, harvestPlants?.preferred]);

  return {
    setCreateUpdateCageHarvest,
    inputMenuDispatch,
    availableHarvestPlants,
    state,
    dispatch,
  };
}

export default useAddCageHarvest;
